<template>
  <div class="animated fadeIn">
    <b-card>
      <template #header>
        <h5 class="m-0">Analise de Brindes e Hospitalidade</h5>
      </template>

      <b-card-body>
        <div class="d-flex align-items-end mb-3">
          <b-form-group class="mr-3 mb-0" label="Pesquisar protocolo">
            <b-input-group>
              <b-form-input
                v-model="filters.protocol"
                @keyup.enter="applyFilters"
              />
              <b-input-group-append>
                <b-button :disabled="!filters.protocol" @click="clearProtocol"
                  >Limpar</b-button
                >
              </b-input-group-append>
            </b-input-group>
          </b-form-group>

          <b-form-group class="mb-0" label="Status">
            <b-form-select
              v-model="filters.status"
              :options="statusOptions"
              @change="applyFilters"
            />
          </b-form-group>
        </div>

        <b-table
          :items="declarations.docs"
          :fields="fields"
          :busy="loading"
          no-local-sorting
          show-empty
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          @sort-changed="onSortChanged"
          @row-clicked="openDetails"
        >
          <template #cell(company)="slotData">
            {{
              (slotData.item.company && slotData.item.company.fantasyName) ||
              (slotData.item.company && slotData.item.company.businessName) ||
              "-"
            }}
          </template>
          <template #cell(submitter)="slotData">
            {{ slotData.item.submitter && slotData.item.submitter.name }}
            {{ slotData.item.submitter && slotData.item.submitter.surname }}
          </template>
          <template #cell(value)="slotData">
            {{ formatMoney(slotData.item.value) }}
          </template>
          <template #cell(status)="slotData">
            <b-badge :variant="statusVariant(slotData.item.status)">
              {{ statusLabel(slotData.item.status) }}
            </b-badge>
          </template>
          <template #cell(createdAt)="slotData">
            {{ formatDate(slotData.item.createdAt) }}
          </template>
          <template #empty>
            <div class="text-muted text-center py-4">
              Nenhuma declaracao para analise.
            </div>
          </template>
        </b-table>

        <b-pagination
          v-model="declarations.page"
          :total-rows="declarations.total"
          :per-page="declarations.limit"
          prev-text="Anterior"
          next-text="Proximo"
          align="center"
          class="mt-3"
        />
      </b-card-body>
    </b-card>

    <b-modal
      v-model="showDetailModal"
      title="Detalhes da declaracao"
      hide-footer
    >
      <div v-if="selectedDeclaration">
        <p><strong>Protocolo:</strong> {{ selectedDeclaration.protocol }}</p>
        <p>
          <strong>Solicitante:</strong>
          {{
            selectedDeclaration.submitter && selectedDeclaration.submitter.name
          }}
          {{
            selectedDeclaration.submitter &&
            selectedDeclaration.submitter.surname
          }}
        </p>
        <p>
          <strong>Empresa:</strong>
          {{
            (selectedDeclaration.company &&
              selectedDeclaration.company.fantasyName) ||
            (selectedDeclaration.company &&
              selectedDeclaration.company.businessName)
          }}
        </p>
        <p><strong>Tipo:</strong> {{ typeLabel(selectedDeclaration.type) }}</p>
        <p>
          <strong>Valor:</strong> {{ formatMoney(selectedDeclaration.value) }}
        </p>
        <p>
          <strong>Quem recebeu:</strong> {{ selectedDeclaration.recipientName }}
        </p>
        <p>
          <strong>Empresa que enviou:</strong>
          {{ selectedDeclaration.senderCompany }}
        </p>
        <p>
          <strong>Data:</strong> {{ formatDate(selectedDeclaration.offeredAt) }}
        </p>
        <p>
          <strong>Centro de custo/Area:</strong>
          {{ selectedDeclaration.costCenter }}
        </p>
        <p><strong>Contexto:</strong> {{ selectedDeclaration.context }}</p>

        <div
          v-if="selectedDeclaration.files && selectedDeclaration.files.length"
        >
          <strong>Anexos:</strong>
          <div>
            <b-button
              v-for="file in selectedDeclaration.files"
              :key="file._id"
              variant="link"
              class="pl-0"
              @click="downloadFile(selectedDeclaration._id, file._id)"
            >
              <i class="fa fa-paperclip"></i> {{ file.name }}
            </b-button>
          </div>
        </div>

        <b-form-group
          v-if="selectedDeclaration.status === 'submitted'"
          label="Comentario da analise (opcional na aprovacao)"
        >
          <b-form-textarea v-model="decision.comment" rows="3" />
        </b-form-group>

        <b-form-group
          v-if="selectedDeclaration.status === 'submitted'"
          label="Parecer da reprovacao"
        >
          <b-form-textarea
            v-model="decision.reason"
            rows="3"
            placeholder="Obrigatorio apenas para reprovar"
          />
        </b-form-group>

        <div
          class="d-flex justify-content-end"
          v-if="selectedDeclaration.status === 'submitted'"
        >
          <b-button
            variant="danger"
            class="mr-2"
            :disabled="sendingDecision"
            @click="rejectSelected"
          >
            <b-spinner v-if="sendingDecision" small class="mr-1" />
            Reprovar
          </b-button>
          <b-button
            variant="success"
            :disabled="sendingDecision"
            @click="approveSelected"
          >
            <b-spinner v-if="sendingDecision" small class="mr-1" />
            Aprovar
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  approveGiftsHospitalityDeclaration,
  getGiftsHospitalityDeclaration,
  getGiftsHospitalityDeclarations,
  giftsHospitalityFileDownloadUrl,
  rejectGiftsHospitalityDeclaration,
} from "../../api/gifts-hospitality";

export default {
  name: "GiftsHospitalityAdmin",
  data() {
    return {
      loading: false,
      sendingDecision: false,
      showDetailModal: false,
      selectedDeclaration: null,
      sortBy: "createdAt",
      sortDesc: true,
      filters: {
        protocol: "",
        status: "",
      },
      decision: {
        comment: "",
        reason: "",
      },
      statusOptions: [
        { value: "", text: "Todos" },
        { value: "submitted", text: "Enviado" },
        { value: "approved", text: "Aprovado" },
        { value: "rejected", text: "Reprovado" },
      ],
      declarations: {
        docs: [],
        page: 1,
        limit: 15,
        total: 0,
      },
      fields: [
        { key: "protocol", label: "Protocolo", sortable: true },
        { key: "company", label: "Empresa", sortable: true },
        { key: "submitter", label: "Solicitante", sortable: true },
        { key: "value", label: "Valor", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "createdAt", label: "Data", sortable: true },
      ],
    };
  },
  watch: {
    "declarations.page"() {
      this.fetchDeclarations();
    },
  },
  mounted() {
    this.fetchDeclarations();
  },
  methods: {
    typeLabel(type) {
      return type === "hospitality" ? "Hospitalidade" : "Brinde";
    },
    statusLabel(status) {
      const labels = {
        submitted: "Enviado",
        approved: "Aprovado",
        rejected: "Reprovado",
      };
      return labels[status] || status;
    },
    statusVariant(status) {
      const variants = {
        submitted: "warning",
        approved: "success",
        rejected: "danger",
      };
      return variants[status] || "secondary";
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleString("pt-BR");
    },
    formatMoney(value) {
      if (!Number.isFinite(value)) return "-";
      return value.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    onSortChanged(sortOptions) {
      if (sortOptions.sortBy) {
        this.sortBy = sortOptions.sortBy;
        this.sortDesc = sortOptions.sortDesc || false;
      }
      this.fetchDeclarations();
    },
    applyFilters() {
      this.declarations.page = 1;
      this.fetchDeclarations();
    },
    clearProtocol() {
      this.filters.protocol = "";
      this.applyFilters();
    },
    async fetchDeclarations() {
      this.loading = true;
      try {
        const response = await getGiftsHospitalityDeclarations({
          page: this.declarations.page,
          size: this.declarations.limit,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
          status: this.filters.status || undefined,
          protocol: this.filters.protocol || undefined,
        });
        this.declarations = {
          ...this.declarations,
          ...response.data,
        };
      } catch (error) {
        this.$bvToast.toast("Erro ao carregar declaracoes.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.loading = false;
      }
    },
    async openDetails(item) {
      try {
        const response = await getGiftsHospitalityDeclaration(item._id);
        this.selectedDeclaration = response.data;
        this.decision.comment = "";
        this.decision.reason = "";
        this.showDetailModal = true;
      } catch (error) {
        this.$bvToast.toast("Erro ao carregar detalhes da declaracao.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      }
    },
    downloadFile(declarationId, fileId) {
      window.open(
        giftsHospitalityFileDownloadUrl(declarationId, fileId),
        "_blank"
      );
    },
    async approveSelected() {
      if (!this.selectedDeclaration) {
        return;
      }
      this.sendingDecision = true;
      try {
        const response = await approveGiftsHospitalityDeclaration(
          this.selectedDeclaration._id,
          {
            comment: this.decision.comment,
          }
        );
        this.selectedDeclaration = response.data;
        await this.fetchDeclarations();
        this.$bvToast.toast("Declaracao aprovada com sucesso.", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
      } catch (error) {
        const message =
          (error && error.response && error.response.data) ||
          "Erro ao aprovar declaracao.";
        this.$bvToast.toast(message, {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.sendingDecision = false;
      }
    },
    async rejectSelected() {
      if (!this.selectedDeclaration) {
        return;
      }
      if (!this.decision.reason.trim()) {
        this.$bvToast.toast("Parecer da reprovacao e obrigatorio.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
        return;
      }
      this.sendingDecision = true;
      try {
        const response = await rejectGiftsHospitalityDeclaration(
          this.selectedDeclaration._id,
          {
            reason: this.decision.reason,
            comment: this.decision.comment,
          }
        );
        this.selectedDeclaration = response.data;
        await this.fetchDeclarations();
        this.$bvToast.toast("Declaracao reprovada com sucesso.", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
      } catch (error) {
        const message =
          (error && error.response && error.response.data) ||
          "Erro ao reprovar declaracao.";
        this.$bvToast.toast(message, {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.sendingDecision = false;
      }
    },
  },
};
</script>
